import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Card, Table, Tabs, Tag, Button, Input, Select, AutoComplete, Modal, Popconfirm, Alert, ConfigProvider,
  Row, Col, Statistic, Space, Typography, Upload, Image, Tooltip, theme, Grid, Drawer, Empty, Segmented,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined, DeleteOutlined, InfoCircleOutlined, DownloadOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, LoadingOutlined,
  HourglassOutlined, ArrowDownOutlined, FilterOutlined, BulbOutlined,
  ControlOutlined, ThunderboltOutlined, SettingOutlined, ExperimentOutlined,
  BarChartOutlined, CameraOutlined, PictureOutlined, CloudSyncOutlined,
  PushpinOutlined, AimOutlined, ImportOutlined, FileExcelOutlined, TableOutlined, CheckOutlined,
} from '@ant-design/icons';
import {
  FacilityAssessment, FacilityAsset, FacilityPhoto, SystemKey, SystemData,
  VisualRating, OperationalRating, MaintenanceRating, RatingLabel, CriterionKey,
} from '../../models/types';
import {
  prepareFacilityPhoto, uploadFacilityPhotoBlob, uploadFacilityPlanFile, deleteStorageFile, deleteStorageFiles,
  assessmentStoragePaths, storageErrorMessage,
} from '../../firebase/storage';
import { enqueuePhoto, getPendingForAssessment, deletePending, type PendingPhoto } from '../../firebase/photoQueue';
import { useOrg } from '../../context/OrgContext';
import PlanViewer, { PlanAsset } from './PlanViewer';
/* cobie.ts pulls in the xlsx library (~140 kB gz) — loaded on demand, never in the main bundle */
import type { CobieImportRow } from './cobie';
import { exportSystemCsv } from './facilityCsv';
import {
  saveFacilityAssessment, updateFacilityAssessment,
  getFacilityAssessments, deleteFacilityAssessment,
  saveFacilityAsset, updateFacilityAsset, deleteFacilityAsset, batchSaveFacilityAssets,
} from '../../firebase/firestore';
import {
  calculateAssetScore, getAssetRating, getBuildingRating, isAssetAssessed, withDerived,
  calculateSystemData, calculateOverallScore, maxPossibleScore, generateRecommendations,
  getDowngradeNote, RATING_DESCRIPTIONS, getCriterionPoints, getAssetConditionText,
  visualNeedsIssues, operationalNeedsIssues, RATING_TAG, RATING_COLORS,
  ratingBucket, RATING_BUCKETS, NEEDS_ATTENTION, type RatingBucket,
} from './facilityCalculations';
import {
  lookupLifetime, LIFE_EXPECTANCY_ROWS, LifetimeRow, classificationLabel,
} from './facilityReferenceData';
import { useIsTouch } from '../../utils/useIsTouch';

/** locked: the project is archived and the viewer isn't its owner/an org admin — mirrors
    firestore.rules' canWriteProjectData. A UX courtesy: disables the write actions so the app
    doesn't invite an edit that would only fail server-side (surfacing as "Save failed" once the
    rejected write reaches Firestore, which is confusing well after the fact). */
interface Props { projectId: string; locked?: boolean; }

const SYSTEM_TABS: { key: SystemKey; label: string; icon: React.ReactNode }[] = [
  { key: 'controls', label: 'Controls', icon: <ControlOutlined /> },
  { key: 'electrical', label: 'Electrical', icon: <ThunderboltOutlined /> },
  { key: 'mechanical', label: 'Mechanical', icon: <SettingOutlined /> },
  { key: 'plumbing', label: 'Plumbing', icon: <ExperimentOutlined /> },
];

const VISUAL_OPTIONS: VisualRating[] = ['New', 'Like New', 'Good', 'Average', 'Poor', 'Critical', 'Failed'];
const OPERATIONAL_OPTIONS: OperationalRating[] = ['Fully Functional', 'Minor Issues', 'Major Issues', 'Failed'];
const MAINTENANCE_OPTIONS: MaintenanceRating[] = ['Above Average', 'Average', 'Below Average', 'None'];

const emptySystem = (): SystemData => ({ assets: [] });

/* Excel leaves RSL to the assessor: the sheet shows "Go To Table" beside a blank M10, they read
   the expected life off that system's life table and subtract the age by hand. We derive it — but
   it has to follow BOTH inputs. Deriving only on description meant the natural entry order
   (description, up in the identity block, then age, further down under the condition
   selections) left a 25-year-old unit showing its full expected life: 10 of 100 points it had not
   earned, and a score that depended on the order the fields were filled in.

   An RSL the assessor typed is left alone. "Still ours" is recognised as the value equalling what
   the formula produced for the previous age, which needs no extra field and so works on records
   saved before this. */
/* RSL = expected life − age, so it needs BOTH: no lifetime for this type, or no age entered yet,
   and there is nothing to derive (undefined = leave RSL alone). Treating a missing age as 0 here
   would hand a fresh import its full expected life while its age scored nothing. */
const derivedRsl = (expectedLife: number | undefined, age: number | null) =>
  expectedLife === undefined || age == null ? undefined : Math.max(0, expectedLife - age);

/* Age / RSL text-field parsing. Only a genuinely blank field means "not entered" (null, scored as
   0 rather than the 20 points a bogus "0 years old" would award — see getAgePoints). Everything
   else the browser hands back must be a real number: an in-progress invalid state — a lone "-" or
   ".", a trailing decimal, a paste the browser didn't fully sanitize — parses via parseFloat to
   NaN, and `NaN || 0` used to fall through to 0 silently, indistinguishable from a real "0" typed
   on purpose. That 0 then autosaves and scores as brand-new equipment: a field-touch (fat finger,
   scroll-drag over the input) could quietly zero out an asset's age or RSL with no message and no
   Undo. An unparseable keystroke now leaves the value exactly as it was instead of guessing 0. */
const parseYearsInput = (raw: string, current: number | null): number | null => {
  if (raw === '') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? Math.max(0, n) : current;
};

const newBlankAsset = (): FacilityAsset => ({
  id: uuidv4(), assetId: '', description: '', location: '',
  manufacturer: '', model: '', serialNumber: '', age: null, remainingServiceLife: null,
  visualInspection: null, operationalTest: null,
  maintenanceRecords: null, maintenanceServiceLevel: null, notes: '',
});

/* Older records kept the equipment type in a `classification` field; the tab already gives the
   system, so that field was removed and its value now lives in `description`. Migrate on load:
   adopt the old classification only where the description is blank (a freeform description the
   assessor typed wins), and drop the dead field so it stops round-tripping through Firestore. */
const migrateAsset = (a: FacilityAsset): FacilityAsset => {
  const { classification, ...rest } = a as FacilityAsset & { classification?: string };
  return { ...rest, description: a.description?.trim() ? a.description : (classification ?? '') };
};
/* The equipment types offered in each system's Description picker — every life-expectancy table
   row as its "Item - Sub-item" label, de-duplicated — for the searchable AutoComplete. Computed
   once; the table is static. */
const DESCRIPTION_OPTIONS: Record<string, { value: string }[]> = Object.fromEntries(
  Object.entries(LIFE_EXPECTANCY_ROWS).map(([sys, rows]) => [
    sys, Array.from(new Set(rows.map(classificationLabel))).map(value => ({ value })),
  ]),
);

const migrateAssessment = (fa: FacilityAssessment): FacilityAssessment => {
  const mig = (sys: SystemData): SystemData => ({ ...sys, assets: sys.assets.map(migrateAsset) });
  return {
    ...fa,
    controls: mig(fa.controls), electrical: mig(fa.electrical),
    mechanical: mig(fa.mechanical), plumbing: mig(fa.plumbing),
  };
};

/* A native `title` attribute only surfaces on hover, so on a tablet the rating
   descriptions were unreachable. Tooltip with a click trigger works for both. */
const RatingBadge: React.FC<{ rating: RatingLabel; score?: number }> = ({ rating, score }) => (
  <Tooltip title={RATING_DESCRIPTIONS[rating]} trigger={['hover', 'click']}>
    <Tag color={RATING_TAG[rating]} style={{ marginInlineEnd: 0 }}>
      {score !== undefined && <b style={{ marginRight: 5 }}>{Math.round(score * 10) / 10}</b>}
      {rating}
    </Tag>
  </Tooltip>
);

/* Multi-select filter chip. A Tag rather than CheckableTag so a selected chip keeps its rating's
   colour (CheckableTag fills with the brand green); role/tabIndex/keys make it a real checkbox for
   keyboard and screen-reader users. Unselected chips carry a colour dot so the row still reads as
   a severity scale. */
const FilterChip: React.FC<{
  checked: boolean; onToggle: () => void; color?: string; dot?: string; touch: boolean; children: React.ReactNode;
}> = ({ checked, onToggle, color, dot, touch, children }) => (
  <Tag
    role="checkbox" aria-checked={checked} tabIndex={0}
    color={checked ? color : undefined}
    icon={checked ? <CheckOutlined /> : undefined}
    onClick={onToggle}
    onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(); } }}
    style={{
      cursor: 'pointer', userSelect: 'none', marginInlineEnd: 0,
      ...(touch ? { padding: '6px 12px', fontSize: 13 } : {}),
      ...(checked ? { fontWeight: 600 } : {}),
    }}
  >
    {!checked && dot && (
      <span aria-hidden style={{
        display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: dot, marginInlineEnd: 6,
      }} />
    )}
    {children}
  </Tag>
);

const toOption = (v: string) => ({ value: v, label: v });

const ASSET_PAGE_SIZE = 20;
/* Sentinel for the in-dropdown "Clear selection" row. The little × AntD renders for allowClear is a
   tiny target on a tablet — tapping near it opens the list instead of clearing — so a real row in
   the list is the reliable touch affordance. Mapped straight back to null, never stored. */
const CLEAR_OPTION = '__clear__';

const FacilityModule: React.FC<Props> = ({ projectId, locked = false }) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const touch = useIsTouch();
  const narrow = !screens.lg;
  /* Side-by-side condition/notes columns need real width, and the content area is the
     window minus the 232px sider and 24px of padding — so this gates on xl (1200), not
     lg (992). At lg a 1024px iPad was putting two columns into 744px, where the left one
     also carries a 62px points gutter. One column in the field. */
  const editorTwoCol = !!screens.xl;
  /* The asset table's declared column widths total ~980px once the description and
     expand columns are counted, but a 1024px iPad leaves only 744px of content after the
     232px sider and 24px padding. Below xl, drop the columns the expanded editor already
     restates rather than squeezing all ten into a sideways scroll. */
  const compactCols = !screens.xl;
  // Storage paths are org-scoped so storage.rules can authorize from the orgId claim.
  const { orgId, isAdmin, isLoading: orgLoading } = useOrg();
  const [assessments, setAssessments] = useState<FacilityAssessment[]>([]);
  const [activeAssessment, setActiveAssessment] = useState<FacilityAssessment | null>(null);
  const [activeTab, setActiveTab] = useState<string>('mechanical');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /* pending = edited, debounce running (unsaved) · saving = write issued, awaiting server ack ·
     queued = ack window passed but the write is safely queued in the local cache · offline = written
     to the device cache, will sync · saved / error = the ack, or a real rejection. */
  type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'queued' | 'offline' | 'error';
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  // Mirror for the background-refresh guard, which runs outside React's render from a Firestore
  // callback and must read the current status, not a stale closure.
  const saveStatusRef = useRef<SaveStatus>('idle');
  saveStatusRef.current = saveStatus;
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [newBuildingName, setNewBuildingName] = useState('');
  const [assetFilter, setAssetFilter] = useState('');
  // Completeness filter for the current tab: focus the list on what still needs doing on the walk.
  const [completeness, setCompleteness] = useState<'all' | 'no-photo' | 'no-age'>('all');
  /* Rating filter (multi-select; empty = all). Like the text filter, and unlike completeness, it is
     kept when you switch system tabs, so "show me what's Failed" follows you through the building. */
  const [ratingFilter, setRatingFilter] = useState<RatingBucket[]>([]);
  const toggleRating = (b: RatingBucket) =>
    setRatingFilter(prev => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b]);
  const clearFilters = () => { setAssetFilter(''); setCompleteness('all'); setRatingFilter([]); };
  const textQuery = assetFilter.trim().toLowerCase();
  const matchesText = (a: FacilityAsset) =>
    !textQuery || [a.assetId, a.description, a.location].some(f => (f ?? '').toLowerCase().includes(textQuery));
  const matchesRating = (a: FacilityAsset) => !ratingFilter.length || ratingFilter.includes(ratingBucket(a));
  /* System-table page, controlled so opening an asset from a pin or the Summary list can land on
     the page that holds it. Tied to the tab + filters it was chosen under: any change to those
     reads as page 1 without every filter handler having to reset it. */
  const [assetPage, setAssetPage] = useState({ sig: '', page: 1 });
  const pageSig = (tab: string, text: string, ratings: RatingBucket[], comp: string) =>
    [tab, text, ratings.join(','), comp].join('|');
  const [scrollToAssetId, setScrollToAssetId] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<readonly React.Key[]>([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  /* Offline photo queue: photos captured with no connection are stashed in IndexedDB and shown
     as "pending" thumbnails until they upload on reconnect. `pending` mirrors the IDB rows for
     the active assessment (with an object URL for preview); `syncing` guards the uploader. */
  const [pending, setPending] = useState<{ localId: string; assetId: string; url: string }[]>([]);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);
  const pendingRef = useRef<{ localId: string; assetId: string; url: string }[]>([]);
  pendingRef.current = pending;
  const [uploadingPlan, setUploadingPlan] = useState(false);
  const [planProgress, setPlanProgress] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [placingAssetId, setPlacingAssetId] = useState<string | null>(null);
  const [highlightAssetId, setHighlightAssetId] = useState<string | null>(null);
  const [importRows, setImportRows] = useState<CobieImportRow[] | null>(null);
  const [importSelected, setImportSelected] = useState<React.Key[]>([]);
  /* The in-app "Go To Table": which asset opened the life-expectancy table, so a row can be
     applied straight back to its RSL. */
  const [lifeTable, setLifeTable] = useState<{ key: SystemKey; assetId: string } | null>(null);
  const [lifeTableFilter, setLifeTableFilter] = useState('');
  /* ── Autosave ──
     Edits debounce 1.5 s, then the whole assessment is written once. Three rules a field tablet in a
     mechanical room needs:
     - Never lose an edit to navigation. The latest unsaved snapshot lives in unsavedRef and is
       flushed — written immediately, no debounce — on unmount, on pagehide, and when the tab is
       hidden (iPadOS discards a backgrounded Safari tab the moment the camera app opens).
     - Never write a stale snapshot. A save that outlives its ack window is NOT retried: with
       persistentLocalCache it is already durably queued and the SDK commits it on reconnect;
       re-issuing a captured snapshot 8 s later reverted every edit made since. Only
       pendingRef.current is ever written, never a closure.
     - Never call queued work a failure. A timeout, or being offline, reads "will sync", and the
       original promise is kept so the status flips to Saved on the real ack. Only a true rejection
       (permission denied, invalid data) is an error. */
  const SAVE_DEBOUNCE_MS = 1500;
  const SAVE_ACK_TIMEOUT_MS = 20000;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsavedRef = useRef<FacilityAssessment | null>(null);   // latest edited, not yet written
  const writeSeqRef = useRef(0);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  /* Load the project's assessments once. getFacilityAssessments is cache-first: instant from the
     local cache on a revisit (no server round-trip on a weak field connection), with a retryable
     server read inside it only when nothing is cached yet (fresh install). Gated on OrgContext so
     the first read never races the auth token on a cold relaunch. loadedProject is set only on
     success, so this never re-runs or resets the active selection once the data is in hand, but a
     failed first attempt is free to retry when the connection recovers. */
  const loadedProjectRef = useRef<string | null>(null);
  /* `slow` gates the spinner: a cache-first read resolves in milliseconds, and flashing a loading
     card for one frame — then re-rendering the whole module — was a visible stutter on every
     navigation. Nothing is drawn until either the data lands or 300 ms pass. */
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (orgLoading || loadedProjectRef.current === projectId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const slowTimer = setTimeout(() => { if (!cancelled) setSlow(true); }, 300);
    /* Background refresh (fires once, after the instant cache paint): apply the fresh server copy so
       a second device or a passive view picks up assets added elsewhere — but never over a live
       edit. Skip if the user has an unsaved snapshot pending or a write still in flight; otherwise
       update the list and re-point the active selection at the same building's fresh copy. */
    const applyRefresh = (fresh: FacilityAssessment[]) => {
      if (cancelled) return;
      if (unsavedRef.current || assetPendingRef.current.size > 0) return;
      if (saveStatusRef.current !== 'idle' && saveStatusRef.current !== 'saved') return;
      const migrated = fresh.map(migrateAssessment);
      setAssessments(migrated);
      const active = activeRef.current;
      if (active) {
        const match = migrated.find(a => a.id === active.id);
        if (match) setActiveAssessment(match);
      } else if (migrated.length > 0) {
        setActiveAssessment(migrated[0]);
      }
    };
    getFacilityAssessments(projectId, applyRefresh)
      .then(list => {
        if (cancelled) return;
        loadedProjectRef.current = projectId;
        const migrated = list.map(migrateAssessment);
        setAssessments(migrated);
        if (migrated.length > 0) setActiveAssessment(migrated[0]);
      })
      .catch(() => { if (!cancelled) setError('Failed to load assessments'); })
      .finally(() => { clearTimeout(slowTimer); if (!cancelled) { setLoading(false); setSlow(false); } });
    return () => { cancelled = true; clearTimeout(slowTimer); };
  }, [projectId, orgLoading]);

  /* True while ANY write — the assessment-level doc or any individual asset — is still unflushed,
     so the two save paths below can share one status indicator without one path's settle() masking
     the other's pending edit. */
  const anyPending = useCallback(() => !!unsavedRef.current || assetPendingRef.current.size > 0, []);
  /* The two flush paths share one status indicator and one sequence counter, so an asset write
     that was rejected must not be papered over by an assessment-level write that acks a moment
     later. A rejection is sticky until the next edit is scheduled. */
  const hadErrorRef = useRef(false);
  const settleStatus = useCallback((seq: number, status: SaveStatus) => {
    if (seq !== writeSeqRef.current || anyPending()) return;
    if (status === 'error') hadErrorRef.current = true;
    const shown: SaveStatus = hadErrorRef.current ? 'error' : status;
    setSaveStatus(shown);
    if (shown === 'saved') {
      setTimeout(() => {
        if (seq === writeSeqRef.current && !anyPending() && !hadErrorRef.current) setSaveStatus('idle');
      }, 3000);
    }
  }, [anyPending]);

  /* Write whatever is pending right now. Safe to call from any of the flush points; a no-op when
     nothing is pending. Each write gets a sequence number so only the most recent one may report
     Saved/error, and never over a newer 'pending'. */
  const flushSave = useCallback(() => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    const snap = unsavedRef.current;
    if (!snap?.id) return;
    unsavedRef.current = null;
    const seq = ++writeSeqRef.current;
    const { id, projectId: pid, createdAt, ...rest } = snap;

    // Issue the write: the local cache accepts it now; the promise is the server ack.
    const ack = updateFacilityAssessment(pid, id, rest);
    ack.then(() => settleStatus(seq, 'saved')).catch(() => settleStatus(seq, 'error'));

    if (!navigator.onLine) { setSaveStatus('offline'); return; }
    setSaveStatus('saving');
    // Past the ack window the write is still queued in the SDK — say so; don't fail or retry.
    setTimeout(() => {
      if (seq === writeSeqRef.current && !anyPending()) {
        setSaveStatus(s => (s === 'saving' ? 'queued' : s));
      }
    }, SAVE_ACK_TIMEOUT_MS);
  }, [settleStatus, anyPending]);

  /* Per-asset autosave — same debounce/flush/status rhythm as flushSave above, but each pending
     asset is written to its own Firestore document (see updateFacilityAsset) instead of one shared
     whole-assessment write. That is the entire fix for the "one device per building" data loss:
     two people editing two different assets now each own a separate document, so neither's save can
     ever overwrite the other's. Destination (project/assessment id) is captured into the map entry
     at schedule time, not read from activeRef at flush time — the user may switch buildings while
     an edit is still debouncing, and the write must still land on the assessment it was made on. */
  const assetPendingRef = useRef(new Map<string, { projectId: string; assessmentId: string; systemKey: SystemKey; asset: FacilityAsset }>());
  const assetSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushAssetSaves = useCallback(() => {
    if (assetSaveTimerRef.current) { clearTimeout(assetSaveTimerRef.current); assetSaveTimerRef.current = null; }
    const pending = assetPendingRef.current;
    if (pending.size === 0) return;
    const entries = Array.from(pending.values());
    pending.clear();
    const seq = ++writeSeqRef.current;

    const ack = Promise.all(entries.map(({ projectId: pid, assessmentId, systemKey, asset }) =>
      updateFacilityAsset(pid, assessmentId, asset.id, asset, systemKey)));
    ack.then(() => settleStatus(seq, 'saved')).catch(() => settleStatus(seq, 'error'));

    if (!navigator.onLine) { setSaveStatus('offline'); return; }
    setSaveStatus('saving');
    setTimeout(() => {
      if (seq === writeSeqRef.current && !anyPending()) {
        setSaveStatus(s => (s === 'saving' ? 'queued' : s));
      }
    }, SAVE_ACK_TIMEOUT_MS);
  }, [settleStatus, anyPending]);

  const scheduleAssetSave = useCallback((systemKey: SystemKey, asset: FacilityAsset) => {
    const aa = activeRef.current;
    if (!aa?.id || locked) return;
    assetPendingRef.current.set(asset.id, { projectId: aa.projectId, assessmentId: aa.id, systemKey, asset });
    hadErrorRef.current = false;
    setSaveStatus('pending');
    if (assetSaveTimerRef.current) clearTimeout(assetSaveTimerRef.current);
    assetSaveTimerRef.current = setTimeout(flushAssetSaves, SAVE_DEBOUNCE_MS);
  }, [flushAssetSaves, locked]);

  const scheduleAutoSave = useCallback((updated: FacilityAssessment) => {
    if (!updated.id) return;
    unsavedRef.current = updated;
    hadErrorRef.current = false;
    setSaveStatus('pending');   // visible the instant the edit lands, not 1.5 s later
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushSave, SAVE_DEBOUNCE_MS);
  }, [flushSave]);

  // Flush — don't cancel — on every way a field tablet loses the page. Both save paths (whole
  // assessment fields, individual assets) need to go out together.
  const flushAll = useCallback(() => { flushSave(); flushAssetSaves(); }, [flushSave, flushAssetSaves]);
  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushAll(); };
    window.addEventListener('pagehide', flushAll);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flushAll);
      document.removeEventListener('visibilitychange', onVisibility);
      flushAll();   // unmount: write the pending snapshots instead of dropping them
    };
  }, [flushAll]);

  /* Local state only. Asset changes are persisted per asset by the callers (scheduleAssetSave /
     saveFacilityAsset / deleteFacilityAsset) — this must NOT also schedule the assessment-level
     save: that write carries plans/notes/info from this device's snapshot, so an asset keystroke
     here would overwrite a plan sheet another device had just uploaded. Score/rating are display
     values recomputed on every read, so nothing here needs writing. */
  const updateSystem = useCallback((key: SystemKey, assets: FacilityAsset[]) => {
    setActiveAssessment(prev => {
      if (!prev) return prev;
      const systemResult = calculateSystemData(assets);
      const updated = { ...prev, [key]: systemResult };
      const overall = calculateOverallScore(updated);
      return { ...updated, overallScore: overall, overallRating: getBuildingRating(overall, maxPossibleScore(updated)) };
    });
  }, []);

  const handleCreateAssessment = async () => {
    if (!newBuildingName.trim() || locked) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const assessment: Omit<FacilityAssessment, 'id'> = {
        projectId, buildingName: newBuildingName.trim(),
        assessmentDate: now.split('T')[0],
        controls: emptySystem(), electrical: emptySystem(),
        mechanical: emptySystem(), plumbing: emptySystem(),
        overallScore: 0, overallRating: 'Failed',
        createdAt: now, updatedAt: now,
      };
      /* The id is minted client-side and the write is queued in the local cache before this
         returns, so the new building appears immediately — online or not. `ack` is the server
         acknowledgement: offline it resolves on reconnect, and only a real rejection (permission,
         bad data) is an error. Awaiting it here, as before, made offline creation fail after a 20 s
         timeout even though the document was safely queued. */
      const { id, ack } = saveFacilityAssessment(assessment);
      const created = { ...assessment, id };
      setAssessments(prev => [created, ...prev]);
      setActiveAssessment(created);
      setShowCreate(false); setNewBuildingName('');
      ack.catch(err => { console.error('Create assessment rejected', err); setError('The new assessment could not be saved to the server.'); });
    } catch { setError('Failed to create assessment'); }
    finally { setSaving(false); }
  };

  /* Delete the document first — that is the action the user asked for, and a document left pointing
     at deleted files is worse than a file left without a document. Then clean up everything the
     assessment owned: its photos and plan sheets in Storage, and any photos still queued on this
     device for it (which would otherwise upload later into a bucket path nobody references). Storage
     cleanup is best-effort; a partial failure is reported, not hidden, so orphans are never silent. */
  const handleDeleteAssessment = async (id: string) => {
    if (locked) return;
    const doomed = assessments.find(a => a.id === id);
    try {
      await deleteFacilityAssessment(projectId, id);
      setAssessments(prev => prev.filter(a => a.id !== id));
      if (activeAssessment?.id === id) setActiveAssessment(null);
    } catch { setError('Failed to delete'); return; }

    const queued = await getPendingForAssessment(id).catch(() => [] as PendingPhoto[]);
    await Promise.allSettled(queued.map(r => deletePending(r.localId)));
    const { failed } = await deleteStorageFiles(doomed ? assessmentStoragePaths(doomed) : []);
    if (failed) setError(`Assessment deleted, but ${failed} file${failed === 1 ? '' : 's'} could not be removed from storage.`);
  };

  /* Section header bar, matching the banded headers Paul's scorecard uses to separate
     identification data from condition selections. Shared by the asset editor and the
     Facility Info card. */
  const sectionHeader = (label: string) => (
    <div style={{
      background: token.colorFillSecondary, borderRadius: 4, padding: '4px 10px',
      fontSize: 12, fontWeight: 600, letterSpacing: .4, textTransform: 'uppercase',
      color: token.colorTextSecondary, marginBottom: 12,
    }}>{label}</div>
  );

  const handleInfoFieldChange = (field: keyof FacilityAssessment, value: string) => {
    setActiveAssessment(prev => {
      if (!prev || locked) return prev;
      const updated = { ...prev, [field]: value };
      scheduleAutoSave(updated);
      return updated;
    });
  };

  const handleAddAsset = (key: SystemKey) => {
    if (!activeAssessment || locked) return;
    const asset = newBlankAsset();
    updateSystem(key, [...activeAssessment[key].assets, asset]);
    setExpandedKeys(prev => [...prev, asset.id]);
    // A brand-new doc — no read-modify-write race, so this goes straight to Firestore rather than
    // through the debounced per-asset save (which exists to coalesce *edits*, not creates).
    if (activeAssessment.id) {
      saveFacilityAsset(activeAssessment.projectId, activeAssessment.id, key, asset)
        .catch(() => setError('Asset added, but could not be saved to the server.'));
    }
  };

  const openLifeTable = (key: SystemKey, asset: FacilityAsset) => {
    /* Seed the table search with the Item portion of the current description (everything before
       the " - "), so an asset already tied to a table row lands on its Item's variants. Empty when
       nothing is set yet, or when the description is freeform and its Item isn't found. */
    const rows = LIFE_EXPECTANCY_ROWS[key] ?? [];
    const lead = asset.description.split(' - ')[0].trim();
    const match = lead.length > 1 && rows.some(r => r.item.toLowerCase().includes(lead.toLowerCase()));
    setLifeTableFilter(match ? lead : '');
    setLifeTable({ key, assetId: asset.id });
  };

  /* Same shape as the assessment delete: drop the asset from the document, then remove its photos
     from Storage and any photos still queued for it on this device (the queue processor would
     otherwise upload them for an asset that no longer exists and attach them to nothing). */
  const handleDeleteAsset = async (key: SystemKey, assetId: string) => {
    if (!activeAssessment || locked) return;
    const doomed = activeAssessment[key].assets.find(a => a.id === assetId);
    updateSystem(key, activeAssessment[key].assets.filter(a => a.id !== assetId));
    // Drop any edit still debouncing for this asset — it would otherwise flush moments later and
    // recreate the document (updateDoc on a deleted doc fails outright, but only after the fact).
    assetPendingRef.current.delete(assetId);

    if (activeAssessment.id) {
      // Not awaited: the promise is the server ack, which offline never arrives — and the queued-
      // photo cleanup below must still run so a reconnect doesn't upload photos for a deleted asset.
      deleteFacilityAsset(activeAssessment.projectId, activeAssessment.id, assetId)
        .catch(() => setError('Asset removed here, but could not be deleted from the server.'));
      const queued = await getPendingForAssessment(activeAssessment.id).catch(() => [] as PendingPhoto[]);
      await Promise.allSettled(queued.filter(r => r.assetId === assetId).map(r => deletePending(r.localId)));
    }
    const paths = (doomed?.photos ?? []).map(p => p.storagePath).filter((p): p is string => !!p);
    const { failed } = await deleteStorageFiles(paths);
    if (failed) setError(`Asset deleted, but ${failed} photo${failed === 1 ? '' : 's'} could not be removed from storage.`);
  };

  const handleAssetFieldChange = (key: SystemKey, asset: FacilityAsset, field: keyof FacilityAsset, value: FacilityAsset[keyof FacilityAsset]) => {
    if (!activeAssessment || locked) return;
    const updated = { ...asset, [field]: value };
    if (field === 'description' || field === 'age') {
      const next = derivedRsl(lookupLifetime(key, updated.description), updated.age);
      /* Changing the description re-derives outright — a different equipment type implies a
         different life (a freeform description not in the table looks up as undefined and leaves
         RSL alone). Changing age only follows along while the current RSL is still ours. */
      // A blank RSL is by definition not something the assessor typed, so it is "ours" too — this
      // is what lets RSL fill in the moment an age is first entered on an imported asset.
      const ours = field === 'description'
        || asset.remainingServiceLife == null
        || asset.remainingServiceLife === derivedRsl(lookupLifetime(key, asset.description), asset.age);
      if (next !== undefined && ours) updated.remainingServiceLife = next;
    }
    // Recompute score/rating from the current fields (dropped entirely until visual + operational
    // are both set, so an unassessed asset stays blank rather than scoring off its defaults).
    const final = withDerived(updated as FacilityAsset);
    updateSystem(key, activeAssessment[key].assets.map(a => a.id === final.id ? final : a));
    scheduleAssetSave(key, final);
  };

  /* Functional per-asset mutation so photo results land safely even if the user
     kept editing fields while the upload was in flight. Schedules a save of just the touched
     asset's own document (see scheduleAssetSave) — score/rating are local display values only,
     recomputed on every read, so there is nothing else here that needs writing to Firestore. */
  const mutateAsset = useCallback((key: SystemKey, assetId: string, mut: (a: FacilityAsset) => FacilityAsset) => {
    setActiveAssessment(prev => {
      if (!prev) return prev;
      const assets = prev[key].assets.map(a => a.id === assetId ? mut(a) : a);
      const systemResult = calculateSystemData(assets);
      const updated = { ...prev, [key]: systemResult };
      const overall = calculateOverallScore(updated);
      const final = { ...updated, overallScore: overall, overallRating: getBuildingRating(overall, maxPossibleScore(updated)) };
      const touched = systemResult.assets.find(a => a.id === assetId);
      if (touched) scheduleAssetSave(key, touched);
      return final;
    });
  }, [scheduleAssetSave]);

  // Latest assessment for the queue processor to read without re-creating on every edit.
  const activeRef = useRef(activeAssessment);
  activeRef.current = activeAssessment;

  /* Keep the assessments list in step with the active copy. Every edit lands on activeAssessment
     only, but the header Select switches buildings by looking the id up in `assessments` — so
     without this, switching away and back restored the page-load snapshot, and the very next
     autosave wrote that stale copy over everything saved since (silent data loss). Mirroring the
     active copy into its list entry on every change closes that, and also makes the Select label
     follow a renamed building. Same-object entries (load / switch) are left alone to avoid a
     needless re-render. */
  useEffect(() => {
    const active = activeAssessment;
    if (!active?.id) return;
    setAssessments(prev => {
      const i = prev.findIndex(a => a.id === active.id);
      if (i === -1 || prev[i] === active) return prev;
      const next = prev.slice();
      next[i] = active;
      return next;
    });
  }, [activeAssessment]);

  // Load this assessment's pending photos from IndexedDB into previewable state; revoke the
  // object URLs when the assessment changes or the component unmounts.
  useEffect(() => {
    const aid = activeAssessment?.id;
    if (!aid) { setPending([]); return; }
    let cancelled = false;
    getPendingForAssessment(aid).then(recs => {
      if (cancelled) return;
      setPending(recs.map(r => ({ localId: r.localId, assetId: r.assetId, url: URL.createObjectURL(r.blob) })));
    }).catch(() => { /* IDB unavailable (private mode) — pending simply won't show */ });
    return () => {
      cancelled = true;
      pendingRef.current.forEach(p => URL.revokeObjectURL(p.url));
    };
  }, [activeAssessment?.id]);

  /* Upload every queued photo for the active assessment, oldest first, via the same mutateAsset
     path an online capture uses — so there is no separate Firestore write to race the autosave.
     Reads IDB fresh each run and stops on the first failure (still offline / flaky), leaving the
     rest queued. Guarded so concurrent triggers can't double-upload. */
  const processQueue = useCallback(async () => {
    const aa = activeRef.current;
    if (!aa?.id || !navigator.onLine || syncingRef.current) return;
    const recs = await getPendingForAssessment(aa.id).catch(() => []);
    if (recs.length === 0) return;
    syncingRef.current = true; setSyncing(true);
    try {
      for (const r of recs) {
        // The user switched buildings mid-drain: stop, and leave the rest queued for that
        // assessment. Continuing would upload the photo and then attach it to nothing.
        if (activeRef.current?.id !== r.assessmentId) break;
        const sysKey = SYSTEM_TABS.find(t => aa[t.key].assets.some(a => a.id === r.assetId))?.key;
        try {
          if (sysKey) {
            const photo = await uploadFacilityPhotoBlob(r.orgId ?? orgId, r.projectId, r.assessmentId, r.assetId, r.blob);
            mutateAsset(sysKey, r.assetId, a => ({ ...a, photos: [...(a.photos ?? []), photo] }));
          }
          // sysKey missing → the asset was deleted; drop the orphaned pending photo.
          await deletePending(r.localId);
          setPending(prev => {
            const hit = prev.find(p => p.localId === r.localId);
            if (hit) URL.revokeObjectURL(hit.url);
            return prev.filter(p => p.localId !== r.localId);
          });
        } catch {
          break; // upload failed (offline again) — leave this and the rest for next time
        }
      }
    } finally { syncingRef.current = false; setSyncing(false); }
  }, [orgId, mutateAsset]);

  // Drain the queue whenever we're online and something is pending (reconnect, or reopening an
  // assessment that has queued photos).
  useEffect(() => {
    if (online && pending.length > 0) processQueue();
  }, [online, pending.length, processQueue]);

  /* iOS suspends JavaScript while the app is in the background. If the tablet reconnects while
     suspended, no `online` event ever reaches us, and the effect above only re-runs when the
     pending count changes — so queued uploads sat until the next photo was taken. Also drain on
     coming back to the foreground. */
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') processQueue(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', processQueue);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', processQueue);
    };
  }, [processQueue]);

  const removePending = async (localId: string) => {
    await deletePending(localId).catch(() => {});
    setPending(prev => {
      const hit = prev.find(p => p.localId === localId);
      if (hit) URL.revokeObjectURL(hit.url);
      return prev.filter(p => p.localId !== localId);
    });
  };

  const handleAddPhoto = async (key: SystemKey, asset: FacilityAsset, file: File) => {
    if (!activeAssessment?.id || locked) return;
    let blob: Blob;
    try {
      blob = await prepareFacilityPhoto(file);
    } catch (err) {
      console.error('Photo prepare failed', err);
      setError(storageErrorMessage(err, 'Photo upload'));
      return;
    }
    const { projectId, id: assessmentId } = activeAssessment;

    // Online: upload immediately. On failure (usually a dropped connection), fall through to the
    // offline queue rather than losing the shot.
    if (navigator.onLine) {
      setUploadingFor(asset.id);
      try {
        const photo = await uploadFacilityPhotoBlob(orgId, projectId, assessmentId, asset.id, blob);
        mutateAsset(key, asset.id, a => ({ ...a, photos: [...(a.photos ?? []), photo] }));
        return;
      } catch (err) {
        console.warn('Immediate upload failed; queuing for retry', err);
      } finally { setUploadingFor(null); }
    }

    // Offline (or the immediate upload just failed): queue it and show it as pending.
    const localId = uuidv4();
    try {
      await enqueuePhoto({ localId, orgId, projectId, assessmentId, assetId: asset.id, blob, createdAt: new Date().toISOString() });
      setPending(prev => [...prev, { localId, assetId: asset.id, url: URL.createObjectURL(blob) }]);
    } catch (err) {
      console.error('Could not queue photo', err);
      setError('Could not save the photo on this device. Check that private browsing is off, or reconnect and try again.');
    }
  };

  const handleDeletePhoto = async (key: SystemKey, assetId: string, photo: FacilityPhoto) => {
    if (locked) return;
    try {
      await deleteStorageFile(photo.storagePath);
      mutateAsset(key, assetId, a => ({ ...a, photos: (a.photos ?? []).filter(p => p.id !== photo.id) }));
    } catch (err) {
      console.error('Photo delete failed', err);
      setError(storageErrorMessage(err, 'Photo delete'));
    }
  };

  const mutateAssessment = useCallback((mut: (a: FacilityAssessment) => FacilityAssessment) => {
    setActiveAssessment(prev => {
      if (!prev) return prev;
      const updated = mut(prev);
      scheduleAutoSave(updated);
      return updated;
    });
  }, [scheduleAutoSave]);

  // ─── Floor plans & pins ─────────────────────────────────────────────────

  const allAssets: PlanAsset[] = useMemo(() => {
    if (!activeAssessment) return [];
    return SYSTEM_TABS.flatMap(t =>
      activeAssessment[t.key].assets.map(a => ({ ...a, systemKey: t.key })));
  }, [activeAssessment]);

  // Firestore rejects undefined — remove the pin key entirely instead of setting it undefined.
  const stripPin = (a: FacilityAsset): FacilityAsset => {
    const { pin, ...rest } = a;
    return rest as FacilityAsset;
  };

  const handleUploadPlan = async (file: File) => {
    if (!activeAssessment?.id || locked) return;
    setUploadingPlan(true);
    setPlanProgress('');
    try {
      // A PDF expands into one plan per sheet; an image stays a single plan.
      const newPlans = await uploadFacilityPlanFile(
        orgId, activeAssessment.projectId, activeAssessment.id, file,
        (done, total) => { if (total > 1) setPlanProgress(`sheet ${done}/${total}`); },
      );
      if (newPlans.length) {
        mutateAssessment(a => ({ ...a, plans: [...(a.plans ?? []), ...newPlans] }));
        setSelectedPlanId(newPlans[0].id);
      }
    } catch (err) {
      console.error('Plan upload failed', err);
      setError(storageErrorMessage(err, 'Plan upload'));
    }
    finally { setUploadingPlan(false); setPlanProgress(''); }
  };

  const handleDeletePlan = async (planId: string) => {
    if (locked) return;
    const plan = activeAssessment?.plans?.find(p => p.id === planId);
    if (!plan) return;
    const stripSystem = (sys: SystemData): SystemData => ({
      ...sys,
      assets: sys.assets.map(a => a.pin?.planId === planId ? stripPin(a) : a),
    });
    try {
      await deleteStorageFile(plan.storagePath);
      mutateAssessment(a => ({
        ...a,
        plans: (a.plans ?? []).filter(p => p.id !== planId),
        controls: stripSystem(a.controls),
        electrical: stripSystem(a.electrical),
        mechanical: stripSystem(a.mechanical),
        plumbing: stripSystem(a.plumbing),
      }));
      // The assessment-level save above no longer carries asset data, so each un-pinned asset's
      // own document has to be written too (updateFacilityAsset deletes the removed pin key).
      SYSTEM_TABS.forEach(t => {
        (activeAssessment?.[t.key].assets ?? [])
          .filter(a => a.pin?.planId === planId)
          .forEach(a => scheduleAssetSave(t.key, stripPin(a)));
      });
      setSelectedPlanId(prev => (prev === planId ? null : prev));
    } catch { setError('Failed to delete plan'); }
  };

  const handlePlacePin = (asset: PlanAsset, planId: string, x: number, y: number) => {
    if (locked) return;
    mutateAsset(asset.systemKey, asset.id, a => ({ ...a, pin: { planId, x, y } }));
    setPlacingAssetId(null);
    setHighlightAssetId(asset.id);
  };

  const handleRemovePin = (asset: PlanAsset) => {
    if (locked) return;
    mutateAsset(asset.systemKey, asset.id, stripPin);
  };

  /* Open an asset in its system tab: expanded, on the page that holds it, scrolled into view. Used
     by plan pins and the Summary rating list. Text and rating filters carry across tabs, so if they
     would hide this asset they are cleared rather than opening a tab where it can't be seen. */
  const openAsset = (key: SystemKey, assetId: string) => {
    const assets = activeAssessment?.[key].assets ?? [];
    const asset = assets.find(a => a.id === assetId);
    if (!asset) return;
    const keep = matchesText(asset) && matchesRating(asset);
    if (!keep) { setAssetFilter(''); setRatingFilter([]); }
    const list = keep ? assets.filter(a => matchesText(a) && matchesRating(a)) : assets;
    setAssetPage({
      sig: pageSig(key, keep ? assetFilter : '', keep ? ratingFilter : [], 'all'),
      page: Math.floor(Math.max(0, list.findIndex(a => a.id === assetId)) / ASSET_PAGE_SIZE) + 1,
    });
    setCompleteness('all');
    setActiveTab(key);
    setExpandedKeys(prev => (prev.includes(assetId) ? prev : [...prev, assetId]));
    setScrollToAssetId(assetId);
  };

  const handleOpenAssetFromPlan = (asset: PlanAsset) => openAsset(asset.systemKey, asset.id);

  // Bring an opened asset's row into view once its tab has rendered.
  useEffect(() => {
    if (!scrollToAssetId) return;
    const t = window.setTimeout(() => {
      document.querySelector(`[data-row-key="${CSS.escape(scrollToAssetId)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollToAssetId(null);
    }, 50);
    return () => window.clearTimeout(t);
  }, [scrollToAssetId, activeTab]);

  // ─── Rating filter chips (system tabs + Summary) ────────────────────────

  const countBuckets = (assets: FacilityAsset[]) => {
    const counts = new Map<RatingBucket, number>();
    assets.forEach(a => { const b = ratingBucket(a); counts.set(b, (counts.get(b) ?? 0) + 1); });
    return counts;
  };

  /* Multi-select, most severe first; "Needs attention" picks Failed + Critical + Poor. A selected
     rating stays on screen at 0 (e.g. after switching tabs) so it can always be turned off again. */
  const renderRatingChips = (counts: Map<RatingBucket, number>, style: React.CSSProperties) => {
    const shownBuckets = RATING_BUCKETS.filter(b => (counts.get(b) ?? 0) > 0 || ratingFilter.includes(b));
    const needsN = NEEDS_ATTENTION.reduce((n, r) => n + (counts.get(r) ?? 0), 0);
    const needsOn = ratingFilter.length === NEEDS_ATTENTION.length && NEEDS_ATTENTION.every(r => ratingFilter.includes(r));
    return (
      <div role="group" aria-label="Filter by rating"
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', ...style }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Rating</Typography.Text>
        {(needsN > 0 || needsOn) && (
          <FilterChip touch={touch} checked={needsOn} color="red" dot={RATING_COLORS.Failed}
            onToggle={() => setRatingFilter(needsOn ? [] : [...NEEDS_ATTENTION])}>
            Needs attention ({needsN})
          </FilterChip>
        )}
        {shownBuckets.map(b => (
          <FilterChip key={b} touch={touch} checked={ratingFilter.includes(b)} onToggle={() => toggleRating(b)}
            color={b === 'unassessed' ? 'default' : RATING_TAG[b]}
            dot={b === 'unassessed' ? token.colorBorder : RATING_COLORS[b]}>
            {b === 'unassessed' ? 'Not assessed' : b} ({counts.get(b) ?? 0})
          </FilterChip>
        ))}
        {ratingFilter.length > 0 && (
          <Button type="link" size="small" onClick={() => setRatingFilter([])} style={{ paddingInline: 4 }}>
            Clear
          </Button>
        )}
      </div>
    );
  };

  // ─── COBie import/export ────────────────────────────────────────────────

  const handleParseCobie = async (file: File) => {
    try {
      const { parseCobieWorkbook } = await import('./cobie');
      const rows = await parseCobieWorkbook(file);
      if (!rows.length) { setError('No components found in that workbook.'); return; }
      setImportRows(rows);
      setImportSelected(rows.map(r => r.key));
    } catch (e: any) {
      setError(e?.message || 'Could not read that file as a COBie workbook.');
    }
  };

  const handleImportConfirm = () => {
    if (!importRows || !activeAssessment) return;
    const chosen = importRows.filter(r => importSelected.includes(r.key));
    if (chosen.length) {
      // Built once here (not inside the setState updater below) so the exact same asset objects
      // go both into local state and into the Firestore batch create.
      const bySystem: { systemKey: SystemKey; asset: FacilityAsset }[] = [];
      (['controls', 'electrical', 'mechanical', 'plumbing'] as SystemKey[]).forEach(s => {
        importRows
          .filter(r => r.system === s && importSelected.includes(r.key))
          .forEach(r => bySystem.push({
            systemKey: s,
            asset: {
              ...newBlankAsset(),
              assetId: r.assetId, description: r.description, location: r.location,
              manufacturer: r.manufacturer, model: r.model, serialNumber: r.serialNumber,
            },
          }));
      });
      mutateAssessment(prev => {
        const next = { ...prev };
        (['controls', 'electrical', 'mechanical', 'plumbing'] as SystemKey[]).forEach(s => {
          const newAssets = bySystem.filter(e => e.systemKey === s).map(e => e.asset);
          if (!newAssets.length) return;
          next[s] = calculateSystemData([...prev[s].assets, ...newAssets]);
        });
        const overall = calculateOverallScore(next);
        return { ...next, overallScore: overall, overallRating: getBuildingRating(overall, maxPossibleScore(next)) };
      });
      // New docs only — no read-modify-write race, so one batch create is enough (see
      // batchSaveFacilityAssets); the assessment-level autosave above never touches asset data.
      if (activeAssessment.id) {
        batchSaveFacilityAssets(activeAssessment.projectId, activeAssessment.id, bySystem)
          .catch(() => setError('Assets imported, but could not be saved to the server.'));
      }
    }
    setImportRows(null);
  };

  /* Exports what the list shows: all of the system's assets, or just the filtered rows (tagged
     "-filtered" in the filename so a partial export is never mistaken for the whole system). */
  const exportCsv = (key: SystemKey, assets?: FacilityAsset[]) => {
    if (!activeAssessment) return;
    exportSystemCsv(activeAssessment.buildingName ?? '', key, assets ?? activeAssessment[key].assets,
      assets ? { suffix: 'filtered' } : {});
  };

  /* Mirrors Excel's Facility Info tab (B2:B22) — building, primary contact, two alternates and the
     assessor block — grouped the way that sheet groups them. Building Type, Square Footage and
     Assessment Date have no Excel counterpart and are kept as additions. */
  const infoGroups: { title: string; fields: { field: keyof FacilityAssessment; label: string; type?: string }[] }[] = [
    { title: 'Building', fields: [
      { field: 'owner', label: 'Owner' },
      { field: 'street', label: 'Street' },
      { field: 'cityStateZip', label: 'City, State, Zip' },
      { field: 'buildingType', label: 'Building Type' },
      { field: 'squareFootage', label: 'Square Footage' },
    ] },
    { title: 'Contact', fields: [
      { field: 'contactName', label: 'Contact Name' },
      { field: 'contactPhone', label: 'Office Phone' },
      { field: 'contactCell', label: 'Cell Phone' },
      { field: 'contactEmail', label: 'E-Mail' },
    ] },
    { title: 'Alternate #1 Contact', fields: [
      { field: 'alt1Name', label: 'Contact Name' },
      { field: 'alt1Phone', label: 'Office Phone' },
      { field: 'alt1Cell', label: 'Cell Phone' },
      { field: 'alt1Email', label: 'E-Mail' },
    ] },
    { title: 'Alternate #2 Contact', fields: [
      { field: 'alt2Name', label: 'Contact Name' },
      { field: 'alt2Phone', label: 'Office Phone' },
      { field: 'alt2Cell', label: 'Cell Phone' },
      { field: 'alt2Email', label: 'E-Mail' },
    ] },
    { title: 'Evaluation Assessor Information', fields: [
      { field: 'assessorName', label: 'Contact Name' },
      { field: 'assessorPhone', label: 'Office Phone' },
      { field: 'assessorCell', label: 'Cell Phone' },
      { field: 'assessorEmail', label: 'E-Mail' },
      { field: 'assessmentDate', label: 'Assessment Date', type: 'date' },
    ] },
  ];

  /* `compactHide` marks columns the expanded asset editor also shows, so narrow screens
     can drop them without losing access to the value. */
  type AssetColumn = ColumnsType<FacilityAsset>[number] & { compactHide?: boolean };

  const assetColumns = (key: SystemKey): ColumnsType<FacilityAsset> => ([
    { title: 'ID', dataIndex: 'assetId', width: 90, render: v => <b>{v || '—'}</b> },
    {
      title: 'Description', dataIndex: 'description',
      render: (v, r) => (
        <Space size={6} wrap>
          {v || <Typography.Text type="secondary">— no description —</Typography.Text>}
          {v && r.remainingServiceLife != null && r.remainingServiceLife <= 0 && (
            <Tag color="gold" icon={<HourglassOutlined />}>Past Expected Lifetime</Tag>
          )}
          {(r.photos?.length ?? 0) > 0 && (
            <Tag icon={<PictureOutlined />}>{r.photos!.length}</Tag>
          )}
        </Space>
      ),
    },
    { title: 'Location', dataIndex: 'location', width: 140, ellipsis: true,
      sorter: (a, b) => (a.location || '').localeCompare(b.location || ''),
      showSorterTooltip: { title: 'Sort by location' } },
    { title: 'Age', dataIndex: 'age', width: 64, align: 'right', compactHide: true,
      render: v => v ?? <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'RSL', dataIndex: 'remainingServiceLife', width: 64, align: 'right', compactHide: true,
      render: v => v ?? <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Visual', dataIndex: 'visualInspection', width: 110,
      render: v => v ?? <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Operational', dataIndex: 'operationalTest', width: 140, compactHide: true,
      render: v => v ?? <Typography.Text type="secondary">—</Typography.Text> },
    {
      title: 'Score', dataIndex: 'score', width: 80, align: 'right',
      sorter: (a, b) => (a.score ?? 0) - (b.score ?? 0),
      render: v => <b>{v !== undefined ? Math.round(v * 10) / 10 : '—'}</b>,
    },
    {
      title: 'Rating', dataIndex: 'rating', width: 110,
      render: (v: RatingLabel | undefined) => v ? <RatingBadge rating={v} /> : null,
    },
    {
      // 44px button + cell padding does not fit the old 50px on touch.
      title: '', key: 'actions', width: touch ? 76 : 50, align: 'right',
      render: (_, r) => (
        <Popconfirm
          title="Delete this asset?"
          description={(r.photos?.length ?? 0) > 0
            ? `Its ${r.photos!.length} photo${r.photos!.length === 1 ? '' : 's'} will be removed too.`
            : undefined}
          onConfirm={() => handleDeleteAsset(key, r.id)} okText="Delete" okButtonProps={{ danger: true }}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="Delete asset"
            style={touch ? { width: 44, height: 44 } : undefined} />
        </Popconfirm>
      ),
    },
  ] as AssetColumn[]).filter(c => !(compactCols && c.compactHide));

  const renderAssetEditor = (key: SystemKey, asset: FacilityAsset) => {
    const note = getDowngradeNote(asset);
    const field = (label: string, node: React.ReactNode, required = false) => (
      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          {label}{required && <span style={{ color: token.colorError }}> *</span>}
        </div>
        {node}
      </div>
    );
    const { rows: pointRows, pinned } = getCriterionPoints(asset);
    const assessed = isAssetAssessed(asset);
    const score = asset.score ?? calculateAssetScore(asset);
    const rating = asset.rating ?? getAssetRating(score);

    const noteFor = (k: CriterionKey): string =>
      asset.criterionNotes?.[k]
        // Older records kept these two on standalone fields, before notes went per-criterion.
        ?? (k === 'visualInspection' ? asset.visualIssues
          : k === 'operationalTest' ? asset.operationalIssues : undefined)
        ?? '';

    const setNote = (k: CriterionKey, v: string) =>
      handleAssetFieldChange(key, asset, 'criterionNotes', { ...(asset.criterionNotes ?? {}), [k]: v });

    /* One criterion per row: selection and its points on the left, that criterion's own note on the
       right. Excel pairs them the same way (M7/M8 beside P7:W8, M10/M11 beside P10:W11, …), and
       "List Issues" (N13/N16) is a prompt to write in this row's note, not a separate field. */
    /* A condition dropdown with a touch-friendly "Clear selection" row at the top (only when the
       field has a value) plus the usual × for mouse users. Selecting Clear — or the × — sets the
       field blank. Shared by all four condition selects. */
    const conditionSelect = (
      field: 'visualInspection' | 'operationalTest' | 'maintenanceRecords' | 'maintenanceServiceLevel',
      choices: readonly string[],
    ) => {
      const current = asset[field] as string | null;
      return (
        <Select
          style={{ width: '100%' }}
          allowClear
          value={current ?? undefined}
          placeholder="Select rating…"
          onChange={v => handleAssetFieldChange(key, asset, field, v == null || v === CLEAR_OPTION ? null : v)}
          options={[
            ...(current != null ? [{
              value: CLEAR_OPTION,
              label: (
                <span style={{
                  display: 'block', margin: '0 -12px', padding: '1px 12px 6px',
                  borderBottom: `1px solid ${token.colorSplit}`, color: token.colorTextTertiary,
                }}>Clear selection</span>
              ),
            }] : []),
            ...choices.map(toOption),
          ]}
        />
      );
    };

    const conditionRow = (
      criterion: CriterionKey, label: string, control: React.ReactNode,
      points: number, max: number, opts: { band?: number; requiresIssues?: boolean } = {},
    ) => (
      <div style={{
        display: 'grid', gridTemplateColumns: editorTwoCol ? '1.35fr 1fr' : '1fr',
        gap: 20, marginBottom: 12, alignItems: 'start',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              {label}
              {opts.band !== undefined && (
                <Tooltip
                  trigger={['hover', 'click']}
                  title="The scorecard scores age and remaining life in 5-year bands, so this entry is scored as the nearest band."
                >
                  <span style={{ marginLeft: 6, fontSize: 12, color: token.colorTextTertiary, textDecoration: 'underline dotted' }}>
                    scored as {opts.band} yr
                  </span>
                </Tooltip>
              )}
            </div>
            {control}
          </div>
          <div style={{ width: 62, textAlign: 'right', paddingTop: 20 }}>
            <Tooltip
              trigger={['hover', 'click']}
              title={pinned
                ? 'A forced downgrade set this asset\u2019s score, so individual points did not contribute to the total.'
                : `Points awarded / deducted (max ${max})`}
            >
              <span style={{
                fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                textDecoration: pinned ? 'line-through' : undefined,
                color: pinned ? token.colorTextQuaternary
                  : points < 0 ? token.colorError
                  : points === 0 ? token.colorTextQuaternary
                  : token.colorSuccess,
              }}>{points > 0 ? `+${points}` : points}</span>
            </Tooltip>
          </div>
        </div>
        <div>
          {opts.requiresIssues && (
            <div style={{ fontSize: 13, marginBottom: 6, color: token.colorWarningText, fontWeight: 500 }}>
              List Issues
            </div>
          )}
          <Input.TextArea
            value={noteFor(criterion)}
            onChange={e => setNote(criterion, e.target.value)}
            placeholder={opts.requiresIssues ? 'e.g. Bad condensing fan' : ''}
            autoSize={{ minRows: 2, maxRows: 6 }}
            style={opts.requiresIssues ? { borderColor: token.colorWarning } : undefined}
          />
        </div>
      </div>
    );

    return (
      /* On touch, bump control heights to a ≥44px tap target across the editor — the criterion
         Selects, the Age/RSL/identity inputs, and the small delete buttons. Desktop keeps AntD
         defaults (empty theme is a passthrough that inherits the app theme). */
      <ConfigProvider theme={touch ? { token: { controlHeight: 44, controlHeightSM: 44 } } : {}}>
      <div>
        {sectionHeader('Equipment Identification Data')}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 18 }}>
          {field('Asset ID', <Input value={asset.assetId} onChange={e => handleAssetFieldChange(key, asset, 'assetId', e.target.value)} />)}
          {field('Description', (
            <>
              {/* Description is the equipment type. The system is already the tab this asset lives
                  under, so there is no separate classification field. Pick a type from this system's
                  life-expectancy table — the same table that sets RSL, so the value and its expected
                  life agree and match the scorecard verbatim — via the searchable list or the fuller
                  table drawer (the table icon), or type a plain-text override for anything the table
                  doesn't cover. A freeform description just leaves RSL to be entered manually. */}
              <AutoComplete
                value={asset.description}
                options={DESCRIPTION_OPTIONS[key] ?? []}
                filterOption={(input, opt) =>
                  String(opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                onChange={v => handleAssetFieldChange(key, asset, 'description', v ?? '')}
                status={asset.description ? undefined : 'error'}
                style={{ width: '100%' }}
              >
                <Input
                  placeholder="Choose from the equipment table or type…"
                  suffix={(
                    <Tooltip title="Browse the full life-expectancy table">
                      <TableOutlined
                        onClick={() => openLifeTable(key, asset)}
                        style={{ color: token.colorTextTertiary, cursor: 'pointer' }}
                      />
                    </Tooltip>
                  )}
                />
              </AutoComplete>
              <div style={{ marginTop: 4 }}>
                {asset.description ? (() => {
                  const yrs = lookupLifetime(key, asset.description);
                  return (
                    <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
                      {yrs === undefined
                        ? 'No expected lifetime in the scorecard — enter RSL manually'
                        : `Expected life ${yrs} yr`}
                    </span>
                  );
                })() : (
                  <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
                    Choose from the equipment table, or type a description
                  </span>
                )}
              </div>
            </>
          ), true)}
          {field('Location', <Input value={asset.location} onChange={e => handleAssetFieldChange(key, asset, 'location', e.target.value)} />)}
          {field('Manufacturer', <Input value={asset.manufacturer} onChange={e => handleAssetFieldChange(key, asset, 'manufacturer', e.target.value)} />)}
          {field('Model #', <Input value={asset.model} onChange={e => handleAssetFieldChange(key, asset, 'model', e.target.value)} />)}
          {field('Serial #', <Input value={asset.serialNumber} onChange={e => handleAssetFieldChange(key, asset, 'serialNumber', e.target.value)} />)}
        </div>

        {/* Header pair spanning the same two columns each criterion row uses, so the labels sit
            over their content exactly as the workbook's banded headers do. */}
        <div style={{ display: 'grid', gridTemplateColumns: editorTwoCol ? '1.35fr 1fr' : '1fr', gap: 20 }}>
          {sectionHeader('Assessment Condition Selections')}
          {editorTwoCol && sectionHeader('Assessment Notes and Comments')}
        </div>
        {/* inputMode="decimal" so iOS/iPadOS shows the numeric pad (not the full keyboard) for
            these years-with-optional-fraction fields — faster, fat-finger-proof field entry. */}
        {conditionRow('age', 'Equipment Age (yrs)',
          <Input type="number" min={0} inputMode="decimal" placeholder="—"
            value={asset.age ?? undefined}
            onChange={e => handleAssetFieldChange(key, asset, 'age', parseYearsInput(e.target.value, asset.age))} />,
          pointRows[0].points, pointRows[0].max, { band: pointRows[0].band })}
        {conditionRow('remainingServiceLife', 'Remaining Service Life (yrs)',
          <Input type="number" min={0} inputMode="decimal" placeholder="—"
            value={asset.remainingServiceLife ?? undefined}
            onChange={e => handleAssetFieldChange(key, asset, 'remainingServiceLife',
              parseYearsInput(e.target.value, asset.remainingServiceLife))} />,
          pointRows[1].points, pointRows[1].max, { band: pointRows[1].band })}
        {/* Each condition dropdown carries a "Clear selection" row (see conditionSelect). Clearing
            visual/operational returns the asset to "not assessed". */}
        {conditionRow('visualInspection', 'Visual Inspection',
          conditionSelect('visualInspection', VISUAL_OPTIONS),
          pointRows[2].points, pointRows[2].max,
          { requiresIssues: asset.visualInspection != null && visualNeedsIssues(asset.visualInspection) })}
        {conditionRow('operationalTest', 'Operational Tests',
          conditionSelect('operationalTest', OPERATIONAL_OPTIONS),
          pointRows[3].points, pointRows[3].max,
          { requiresIssues: asset.operationalTest != null && operationalNeedsIssues(asset.operationalTest) })}
        {conditionRow('maintenanceRecords', 'Maintenance Records',
          conditionSelect('maintenanceRecords', MAINTENANCE_OPTIONS),
          pointRows[4].points, pointRows[4].max)}
        {conditionRow('maintenanceServiceLevel', 'Maintenance Service Level',
          conditionSelect('maintenanceServiceLevel', MAINTENANCE_OPTIONS),
          pointRows[5].points, pointRows[5].max)}

        {/* No Excel counterpart — a place for anything that isn't tied to one criterion, and where
            notes recorded before the per-criterion split still live. */}
        <div style={{ marginTop: 4, marginBottom: 4 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>General Notes</div>
          <Input.TextArea
            value={asset.notes}
            onChange={e => handleAssetFieldChange(key, asset, 'notes', e.target.value)}
            placeholder="Anything spanning more than one criterion…"
            autoSize={{ minRows: 2, maxRows: 8 }}
          />
        </div>

        {/* Paul's score footer: score, rating, and the prescriptive action sentence together. */}
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16,
          background: token.colorFillQuaternary, borderRadius: 6,
          padding: '10px 14px', margin: '16px 0 12px',
        }}>
          {assessed ? (
            <>
              <Statistic title="Assessment Score" value={score} precision={1} valueStyle={{ fontSize: 22 }} />
              <div>
                <div style={{ fontSize: 13, color: token.colorTextSecondary, marginBottom: 4 }}>Rating</div>
                <RatingBadge score={score} rating={rating} />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: 13, color: token.colorTextSecondary }}>
                {getAssetConditionText(asset, rating)}
              </div>
            </>
          ) : (
            <div style={{ flex: '1 1 auto', fontSize: 13, color: token.colorTextSecondary }}>
              Not assessed yet — choose a <b>Visual Inspection</b> and <b>Operational Tests</b> rating to calculate a score.
            </div>
          )}
        </div>
        {note && <Alert type="warning" showIcon icon={<ArrowDownOutlined />} message={note} style={{ marginBottom: 12 }} />}
        <div>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            Photos{(asset.photos?.length ?? 0) > 0 ? ` (${asset.photos!.length})` : ''}
            {pending.some(p => p.assetId === asset.id) && (
              <Typography.Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                · {pending.filter(p => p.assetId === asset.id).length} pending upload
              </Typography.Text>
            )}
          </div>
          <Space wrap align="start">
            <Image.PreviewGroup>
              {(asset.photos ?? []).map(p => (
                <div key={p.id} style={{ position: 'relative', display: 'inline-block' }}>
                  {/* Larger on touch so the 44px delete button in the corner does not
                      cover most of the thumbnail. */}
                  <Image
                    src={p.url} alt="Asset photo"
                    width={touch ? 112 : 88} height={touch ? 112 : 88}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                  />
                  <Popconfirm
                    title="Delete this photo?" okText="Delete" okButtonProps={{ danger: true }}
                    onConfirm={() => handleDeletePhoto(key, asset.id, p)} disabled={!online || locked}
                  >
                    <Button
                      size="small" danger type="text" icon={<DeleteOutlined />} disabled={!online || locked}
                      aria-label="Delete photo"
                      style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,.85)', ...(touch ? { width: 44, height: 44 } : {}) }}
                    />
                  </Popconfirm>
                </div>
              ))}
            </Image.PreviewGroup>
            {/* Pending (offline-captured) photos — shown from the local blob until they upload. */}
            {pending.filter(p => p.assetId === asset.id).map(p => (
              <div key={p.localId} style={{ position: 'relative', display: 'inline-block' }}>
                <Image
                  src={p.url} alt="Pending photo" preview={false}
                  width={touch ? 112 : 88} height={touch ? 112 : 88}
                  style={{ objectFit: 'cover', borderRadius: 6, opacity: 0.9 }}
                />
                <Tag color="gold" style={{ position: 'absolute', bottom: 2, left: 2, margin: 0, fontSize: 10, lineHeight: '15px', padding: '0 4px' }}>
                  {syncing ? 'Uploading…' : 'Pending'}
                </Tag>
                <Button
                  size="small" danger type="text" icon={<DeleteOutlined />}
                  aria-label="Remove pending photo"
                  onClick={() => removePending(p.localId)}
                  style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,.85)', ...(touch ? { width: 44, height: 44 } : {}) }}
                />
              </div>
            ))}
            {/* Two capture paths, both feeding handleAddPhoto. "Take Photo" carries
                capture="environment", which on iPadOS/iOS opens the rear camera directly — but
                that same attribute removes the Photo Library / Files options, so a separate
                "Upload" WITHOUT capture is what lets the assessor attach an existing shot. On
                desktop capture is ignored and both behave as a file picker.
                The reason these are disabled used to live in a hover tooltip, which a tablet never
                shows — and offline is the normal state in a mechanical room — so it is stated in
                plain sight below instead. */}
            <Upload
              accept="image/*" capture="environment" multiple showUploadList={false}
              disabled={uploadingFor === asset.id || locked}
              beforeUpload={file => { handleAddPhoto(key, asset, file); return false; }}
            >
              <Button
                type="primary"
                icon={uploadingFor === asset.id ? <LoadingOutlined /> : <CameraOutlined />}
                disabled={uploadingFor === asset.id || locked}
              >
                {uploadingFor === asset.id ? 'Uploading…' : 'Take Photo'}
              </Button>
            </Upload>
            <Upload
              accept="image/*" multiple showUploadList={false}
              disabled={uploadingFor === asset.id || locked}
              beforeUpload={file => { handleAddPhoto(key, asset, file); return false; }}
            >
              <Button
                icon={<PictureOutlined />}
                disabled={uploadingFor === asset.id || locked}
              >
                Upload
              </Button>
            </Upload>
            {(activeAssessment?.plans?.length ?? 0) > 0 && (asset.pin ? (
              <Button icon={<AimOutlined />} onClick={() => {
                setSelectedPlanId(asset.pin!.planId);
                setHighlightAssetId(asset.id);
                setActiveTab('plans');
              }}>Show on Plan</Button>
            ) : (
              <Button icon={<PushpinOutlined />} onClick={() => {
                setPlacingAssetId(asset.id);
                setActiveTab('plans');
              }}>Place on Plan</Button>
            ))}
          </Space>
          {!online && (
            <Typography.Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              <CloudSyncOutlined /> Offline — photos are saved on this device and upload
              automatically when you reconnect, along with ratings, notes and scores.
            </Typography.Text>
          )}
          {online && syncing && pending.some(p => p.assetId === asset.id) && (
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              <CloudSyncOutlined /> Uploading saved photos…
            </Typography.Text>
          )}
          {/* Online, not syncing, yet photos are still queued: an upload failed or a resume trigger
              was missed. Give the assessor a hand-crank instead of making them take another photo. */}
          {online && !syncing && pending.some(p => p.assetId === asset.id) && (
            <div style={{ marginTop: 8 }}>
              <Typography.Text type="warning" style={{ fontSize: 12 }}>
                <CloudSyncOutlined /> {pending.filter(p => p.assetId === asset.id).length} photo(s) waiting to upload.
              </Typography.Text>{' '}
              <Button size="small" onClick={() => processQueue()} disabled={locked}>Retry uploads</Button>
            </div>
          )}
        </div>
      </div>
      </ConfigProvider>
    );
  };

  const renderSystemTab = (key: SystemKey) => {
    if (!activeAssessment) return null;
    const sys = activeAssessment[key];

    // Walk-completeness counts, over the whole system (independent of the text filter below).
    const missingPhoto = (a: FacilityAsset) => (a.photos?.length ?? 0) === 0 && !pending.some(p => p.assetId === a.id);
    const unassessedN = sys.assets.filter(a => !isAssetAssessed(a)).length;
    const noPhotoN = sys.assets.filter(missingPhoto).length;
    const noAgeN = sys.assets.filter(a => a.age == null).length;
    const assessedN = sys.assets.length - unassessedN;

    // If the selected backlog has emptied (you just assessed the last one), fall back to All so you
    // are never stranded on an empty list with the chip gone.
    const activeCount = completeness === 'no-photo' ? noPhotoN
      : completeness === 'no-age' ? noAgeN : -1;
    const effective = completeness !== 'all' && activeCount === 0 ? 'all' : completeness;

    const byText = sys.assets.filter(matchesText);
    const byCompleteness =
      effective === 'no-photo' ? byText.filter(missingPhoto)
      : effective === 'no-age' ? byText.filter(a => a.age == null)
      : byText;

    const filtered = byCompleteness.filter(matchesRating);
    const sig = pageSig(key, assetFilter, ratingFilter, completeness);
    const page = Math.min(assetPage.sig === sig ? assetPage.page : 1,
      Math.max(1, Math.ceil(filtered.length / ASSET_PAGE_SIZE)));

    return (
      <Card styles={{ body: { padding: 0 } }} style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        {/* Wraps rather than compressing: the filter box plus two buttons do not fit
            beside the asset count in an iPad's content width. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', flexWrap: 'wrap' }}>
          <span><b>{sys.assets.length} asset{sys.assets.length !== 1 ? 's' : ''}</b>
            {sys.assets.length > 0 && <> · <b>{assessedN} of {sys.assets.length}</b> assessed</>}
            {sys.rating && <> · avg <b>{Math.round((sys.score ?? 0) * 10) / 10}</b></>}
          </span>
          {sys.assets.length > 0 && sys.rating && <RatingBadge rating={sys.rating} />}
          <div style={{ flex: '1 1 0', minWidth: 0 }} />
          <Input
            allowClear prefix={<FilterOutlined style={{ color: token.colorTextQuaternary }} />}
            placeholder="Filter by ID, description…"
            style={{ flex: '1 1 200px', minWidth: 170, maxWidth: 260 }}
            value={assetFilter} onChange={e => setAssetFilter(e.target.value)}
          />
          {filtered.length < sys.assets.length ? (
            <Button icon={<DownloadOutlined />} onClick={() => exportCsv(key, filtered)} disabled={!filtered.length}>
              Export {filtered.length} shown
            </Button>
          ) : (
            <Button icon={<DownloadOutlined />} onClick={() => exportCsv(key)} disabled={!sys.assets.length}>Export CSV</Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAddAsset(key)} disabled={locked}>Add Asset</Button>
        </div>
        {/* Completeness chips — jump the list to what still needs doing. Only the ones that mean
            something (a non-zero backlog) are offered, so the row stays quiet on a finished system. */}
        {sys.assets.length > 0 && (noPhotoN > 0 || noAgeN > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 24px 14px', flexWrap: 'wrap' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Show</Typography.Text>
            <Segmented
              size="small"
              value={effective}
              onChange={v => setCompleteness(v as typeof completeness)}
              options={[
                { label: `All (${sys.assets.length})`, value: 'all' },
                ...(noPhotoN > 0 ? [{ label: `No photo (${noPhotoN})`, value: 'no-photo' }] : []),
                ...(noAgeN > 0 ? [{ label: `No age (${noAgeN})`, value: 'no-age' }] : []),
              ]}
            />
          </div>
        )}
        {/* Rating counts over the whole system, like the completeness counts. */}
        {sys.assets.length > 0 && renderRatingChips(countBuckets(sys.assets), { padding: '0 24px 14px' })}
        <Table<FacilityAsset>
          rowKey="id"
          size="middle"
          columns={assetColumns(key)}
          dataSource={filtered}
          /* Without this the declared column widths overflow the card and you end up
             scrolling the whole page sideways; AntD only builds a horizontal scroller
             for the table itself when scroll.x is set. */
          scroll={{ x: 'max-content' }}
          pagination={filtered.length > ASSET_PAGE_SIZE ? {
            pageSize: ASSET_PAGE_SIZE, current: page, onChange: p => setAssetPage({ sig, page: p }),
            showTotal: (t, r) => `${r[0]}–${r[1]} of ${t} assets`,
          } : false}
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: setExpandedKeys,
            expandedRowRender: r => renderAssetEditor(key, r),
          }}
          locale={{
            emptyText: sys.assets.length
              ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No assets match these filters">
                  <Button onClick={clearFilters}>Clear filters</Button>
                </Empty>
              )
              : 'No assets yet — add the first one.',
          }}
        />
      </Card>
    );
  };

  /* Building-wide rating filter: the same chips and text filter as the system tabs (and the same
     state, so a selection made here is still on when you open a system), listing matches across
     all four systems. Tapping a row opens that asset in its own tab. */
  const renderBuildingFilter = () => {
    if (!activeAssessment) return null;
    const systemLabel = (k: SystemKey) => SYSTEM_TABS.find(t => t.key === k)?.label ?? k;
    const active = ratingFilter.length > 0 || textQuery !== '';
    // Score/rating recomputed from the fields (never a stored value), worst first.
    const matches = active
      ? allAssets.filter(a => matchesText(a) && matchesRating(a))
          .map(a => ({ ...withDerived(a), systemKey: a.systemKey }))
          .sort((a, b) => RATING_BUCKETS.indexOf(a.rating ?? 'unassessed') - RATING_BUCKETS.indexOf(b.rating ?? 'unassessed')
            || (a.score ?? 0) - (b.score ?? 0))
      : [];
    return (
      <div style={{ marginBottom: 28 }}>
        <Typography.Title level={5}><FilterOutlined /> Assets by rating, all systems</Typography.Title>
        {allAssets.length === 0 ? (
          <Typography.Text type="secondary">No assets yet.</Typography.Text>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <Input
                allowClear prefix={<FilterOutlined style={{ color: token.colorTextQuaternary }} />}
                placeholder="Filter by ID, description…"
                style={{ flex: '1 1 200px', minWidth: 170, maxWidth: 260 }}
                value={assetFilter} onChange={e => setAssetFilter(e.target.value)}
              />
            </div>
            {renderRatingChips(countBuckets(allAssets), { marginBottom: 12 })}
            {active && matches.length > 0 && (
              <Button icon={<DownloadOutlined />} style={{ marginBottom: 12 }}
                onClick={() => exportSystemCsv(activeAssessment.buildingName ?? '', 'all-systems', matches,
                  { system: a => systemLabel((a as PlanAsset).systemKey), suffix: 'filtered' })}>
                Export {matches.length} shown
              </Button>
            )}
            {!active ? (
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                Pick a rating (or type a filter) to list matching assets across all systems.
              </Typography.Text>
            ) : (
              <Table<PlanAsset>
                rowKey="id"
                size="small"
                dataSource={matches}
                scroll={{ x: 'max-content' }}
                onRow={r => ({ onClick: () => openAsset(r.systemKey, r.id), style: { cursor: 'pointer' } })}
                pagination={matches.length > ASSET_PAGE_SIZE
                  ? { pageSize: ASSET_PAGE_SIZE, showTotal: (t, r) => `${r[0]}–${r[1]} of ${t} assets` } : false}
                locale={{
                  emptyText: (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No assets match these filters">
                      <Button onClick={clearFilters}>Clear filters</Button>
                    </Empty>
                  ),
                }}
                columns={[
                  { title: 'System', dataIndex: 'systemKey', width: 110, render: (k: SystemKey) => systemLabel(k) },
                  {
                    // A real button so the row can be opened from the keyboard, not only by tapping it.
                    title: 'ID', dataIndex: 'assetId', width: 100,
                    render: (v, r) => (
                      <Button type="link" size="small" style={{ padding: 0, fontWeight: 600 }}
                        onClick={e => { e.stopPropagation(); openAsset(r.systemKey, r.id); }}>
                        {v || '—'}
                      </Button>
                    ),
                  },
                  { title: 'Description', dataIndex: 'description',
                    render: v => v || <Typography.Text type="secondary">— no description —</Typography.Text> },
                  { title: 'Location', dataIndex: 'location', width: 140, ellipsis: true },
                  { title: 'Score', dataIndex: 'score', width: 70, align: 'right',
                    render: v => v !== undefined ? Math.round(v * 10) / 10 : '—' },
                  { title: 'Rating', dataIndex: 'rating', width: 110,
                    render: (v: RatingLabel | undefined) => v ? <RatingBadge rating={v} /> : <Tag>Not assessed</Tag> },
                ]}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const renderSummaryTab = () => {
    if (!activeAssessment) return null;
    return (
      <Card styles={{ body: { padding: 24 } }} style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        <Typography.Title level={5} style={{ marginTop: 0 }}><BarChartOutlined /> System scores (0 – 100)</Typography.Title>
        <div style={{ maxWidth: 860, marginBottom: 28 }}>
          {SYSTEM_TABS.map(t => {
            const sys = activeAssessment[t.key];
            const score = sys.score ?? 0;
            return (
              <div key={t.key} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 130px', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                <span style={{ fontSize: 13 }}>{t.label}</span>
                <div style={{ height: 12, background: token.colorFillTertiary, borderRadius: 6, position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: 0, width: `${Math.min(100, score)}%`, background: token.colorPrimary, borderRadius: '6px 4px 4px 6px' }} />
                </div>
                <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                  {/* No assessed asset → no score; an empty bar plus "—" reads as not assessed, not 0. */}
                  <b>{sys.score != null ? Math.round(score * 10) / 10 : '—'}</b>
                  <Typography.Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>{sys.rating ?? '—'}</Typography.Text>
                </span>
              </div>
            );
          })}
        </div>
        {renderBuildingFilter()}
        <Typography.Title level={5}><BulbOutlined /> Recommendations</Typography.Title>
        <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
          {generateRecommendations(activeAssessment).map((r, i) => (
            <li key={i} style={{ marginBottom: 6, fontSize: 14 }}>{r}</li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderPlansTab = () => {
    if (!activeAssessment) return null;
    const plans = activeAssessment.plans ?? [];
    const selectedPlan = plans.find(p => p.id === selectedPlanId) ?? plans[0] ?? null;
    const pinnedCount = selectedPlan
      ? allAssets.filter(a => a.pin?.planId === selectedPlan.id).length
      : 0;
    return (
      <Card styles={{ body: { padding: 24 } }} style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {plans.length > 0 && (
            <Select
              style={{ minWidth: 220 }}
              value={selectedPlan?.id}
              options={plans.map(p => ({ value: p.id, label: p.name }))}
              onChange={setSelectedPlanId}
            />
          )}
          {/* Same as the photo button: the offline reason is stated below, not on hover. */}
          <Upload
            accept="image/*,application/pdf" showUploadList={false}
            disabled={!online || uploadingPlan || locked}
            beforeUpload={file => { handleUploadPlan(file); return false; }}
          >
            <Button icon={uploadingPlan ? <LoadingOutlined /> : <PlusOutlined />} disabled={!online || uploadingPlan || locked}>
              {uploadingPlan ? `Uploading${planProgress ? ` ${planProgress}` : ''}…` : 'Upload Plan or PDF'}
            </Button>
          </Upload>
          {selectedPlan && (
            <Popconfirm
              title="Delete this plan? Pins on it will be removed."
              okText="Delete" okButtonProps={{ danger: true }}
              onConfirm={() => handleDeletePlan(selectedPlan.id)} disabled={!online || locked}
            >
              <Button danger icon={<DeleteOutlined />} disabled={!online || locked} aria-label="Delete plan"
                style={touch ? { width: 44, height: 44 } : undefined} />
            </Popconfirm>
          )}
          <div style={{ flex: 1 }} />
          {selectedPlan && (
            <>
              <Typography.Text type="secondary">{pinnedCount} pinned</Typography.Text>
              <Select
                style={{ minWidth: 260 }}
                allowClear showSearch optionFilterProp="label"
                placeholder="Place pin for asset…"
                value={placingAssetId ?? undefined}
                options={allAssets.map(a => ({
                  value: a.id,
                  label: `${a.assetId || '—'} · ${a.description || 'no description'}${a.pin ? ' (pinned)' : ''}`,
                }))}
                onChange={v => setPlacingAssetId(v ?? null)}
              />
            </>
          )}
        </div>
        {/* Same text + rating filter as the other tabs: non-matching pins fade back. Counts are
            over this plan's pins, since that is what the filter acts on here. */}
        {selectedPlan && pinnedCount > 0 && (() => {
          const onPlan = allAssets.filter(a => a.pin?.planId === selectedPlan.id);
          const shown = onPlan.filter(a => matchesText(a) && matchesRating(a)).length;
          return (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <Input
                allowClear prefix={<FilterOutlined style={{ color: token.colorTextQuaternary }} />}
                placeholder="Filter by ID, description…"
                style={{ flex: '1 1 200px', minWidth: 170, maxWidth: 260 }}
                value={assetFilter} onChange={e => setAssetFilter(e.target.value)}
              />
              {renderRatingChips(countBuckets(onPlan), {})}
              {shown < onPlan.length && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {shown} of {onPlan.length} pins match
                </Typography.Text>
              )}
            </div>
          );
        })()}
        <PlanViewer
          plan={selectedPlan}
          assets={allAssets}
          isDimmed={a => !(matchesText(a) && matchesRating(a))}
          placingAsset={allAssets.find(a => a.id === placingAssetId) ?? null}
          highlightAssetId={highlightAssetId}
          onPlacePin={(asset, x, y) => selectedPlan && handlePlacePin(asset, selectedPlan.id, x, y)}
          onRemovePin={handleRemovePin}
          onOpenAsset={handleOpenAssetFromPlan}
        />
        {!online && (
          <Typography.Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
            <CloudSyncOutlined /> Uploading or deleting plans needs a connection. Placing and
            removing pins works offline and syncs later.
          </Typography.Text>
        )}
        <Typography.Text type="secondary" style={{ fontSize: 12.5, display: 'block', marginTop: 10 }}>
          Drag to pan · pinch or scroll to zoom · Reset view returns to fit · tap a pin for its asset · pins are colored by asset rating
        </Typography.Text>
      </Card>
    );
  };

  const tabItems = useMemo(() => {
    if (!activeAssessment) return [];
    return [
      ...SYSTEM_TABS.map(t => {
        const sys = activeAssessment[t.key];
        return {
          key: t.key,
          label: (
            <span>
              {t.icon} {t.label}
              {sys.assets.length > 0 && (
                <Tag style={{ marginLeft: 8 }} color={activeTab === t.key ? 'green' : 'default'}>
                  {sys.score != null ? Math.round(sys.score * 10) / 10 : '—'}
                </Tag>
              )}
            </span>
          ),
          /* Build only the active pane. Building all six eagerly (four 48-row tables, the plan
             viewer decoding a large image, the summary) on every mount was most of the cost of
             navigating back into a project on an iPad; with destroyOnHidden below, an inactive tab
             holds no DOM at all. Table/expanded state lives in module state, so it survives. */
          children: activeTab === t.key ? renderSystemTab(t.key) : null,
        };
      }),
      {
        key: 'plans',
        label: (
          <span>
            <PushpinOutlined /> Plans
            {(activeAssessment.plans?.length ?? 0) > 0 && (
              <Tag style={{ marginLeft: 8 }}>{activeAssessment.plans!.length}</Tag>
            )}
          </span>
        ),
        children: activeTab === 'plans' ? renderPlansTab() : null,
      },
      { key: 'summary', label: <span><BarChartOutlined /> Summary</span>, children: activeTab === 'summary' ? renderSummaryTab() : null },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAssessment, activeTab, assetFilter, completeness, ratingFilter, assetPage, pending, expandedKeys, online, uploadingFor,
      uploadingPlan, planProgress, selectedPlanId, placingAssetId, highlightAssetId]);

  // Blank (not a spinner) while a fast cache read is in flight; spinner only once it's proven slow.
  if (loading) return slow ? <Card loading /> : null;

  return (
    <div>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} style={{ marginBottom: 16 }} />}
      {locked && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message="This assessment is archived"
          description="Editing is restricted to the project's owner and organization admins."
        />
      )}

      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>Facility Assessment</Typography.Title>
            <Typography.Text type="secondary">
              Asset condition assessment{activeAssessment?.buildingName ? ` · ${activeAssessment.buildingName}` : ''}
            </Typography.Text>
          </div>
          {assessments.length > 0 && (
            <Select
              style={{ minWidth: 240 }}
              value={activeAssessment?.id}
              options={assessments.map(a => ({ value: a.id!, label: a.buildingName || 'Untitled' }))}
              onChange={id => setActiveAssessment(assessments.find(a => a.id === id) ?? null)}
            />
          )}
          {/* The parent wraps but this group did not, so five controls overflowed in
              portrait. Left-aligns once wrapped instead of clinging to the right edge. */}
          <div style={{
            marginLeft: narrow ? 0 : 'auto', display: 'flex', alignItems: 'center',
            gap: 8, flexWrap: 'wrap',
          }}>
            {saveStatus === 'pending' && <Typography.Text type="secondary"><HourglassOutlined /> Unsaved changes…</Typography.Text>}
            {saveStatus === 'saving' && <Typography.Text type="secondary"><LoadingOutlined /> Saving…</Typography.Text>}
            {saveStatus === 'saved' && <Typography.Text style={{ color: '#389e0d' }}><CheckCircleOutlined /> Saved</Typography.Text>}
            {saveStatus === 'queued' && <Typography.Text type="warning"><CloudSyncOutlined /> Queued — will sync</Typography.Text>}
            {saveStatus === 'offline' && <Typography.Text type="warning"><CloudSyncOutlined /> Saved on device — will sync</Typography.Text>}
            {saveStatus === 'error' && <Typography.Text type="danger"><ExclamationCircleOutlined /> Save failed</Typography.Text>}
            {activeAssessment && (
              <>
                {/* COBie carries the raw handoff data (and import can bulk-create assets), so it's
                    restricted to org admins — the same isAdmin gate TeamPage/ProjectHub use for
                    their admin-only controls, not a Firestore/Storage rule (nothing else about
                    COBie touches those paths differently per role). */}
                {isAdmin && (
                  <>
                    <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={f => { handleParseCobie(f); return false; }}>
                      <Button icon={<ImportOutlined />}>Import COBie</Button>
                    </Upload>
                    <Button icon={<FileExcelOutlined />} onClick={async () => {
                      const { exportCobieWorkbook } = await import('./cobie');
                      exportCobieWorkbook(activeAssessment);
                    }}>
                      Export COBie
                    </Button>
                  </>
                )}
                <Button icon={<InfoCircleOutlined />} type={showInfo ? 'primary' : 'default'} ghost={showInfo} onClick={() => setShowInfo(s => !s)}>
                  Facility Info
                </Button>
                <Popconfirm
                  title="Delete this assessment?"
                  description={(() => {
                    const photos = assessmentStoragePaths(activeAssessment).length - (activeAssessment.plans?.length ?? 0);
                    const plans = activeAssessment.plans?.length ?? 0;
                    const parts = [
                      photos > 0 && `${photos} photo${photos === 1 ? '' : 's'}`,
                      plans > 0 && `${plans} plan sheet${plans === 1 ? '' : 's'}`,
                    ].filter(Boolean);
                    return parts.length ? `All its assets, and ${parts.join(' and ')}, will be permanently removed.` : 'All its assets will be permanently removed.';
                  })()}
                  onConfirm={() => handleDeleteAssessment(activeAssessment.id!)} okText="Delete" okButtonProps={{ danger: true }}
                  disabled={locked}>
                  <Button danger icon={<DeleteOutlined />} aria-label="Delete assessment" disabled={locked}
                    style={touch ? { width: 44, height: 44 } : undefined} />
                </Popconfirm>
              </>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreate(true)} disabled={locked}>New Assessment</Button>
          </div>
        </div>
      </Card>

      <Modal
        title={`Import COBie — ${importRows?.length ?? 0} components found`}
        open={!!importRows}
        width={960}
        okText={`Import ${importSelected.length} asset${importSelected.length !== 1 ? 's' : ''}`}
        okButtonProps={{ disabled: importSelected.length === 0 }}
        onOk={handleImportConfirm}
        onCancel={() => setImportRows(null)}
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          Review the detected components. The system assignment is a best guess from the
          COBie Type/Category — correct any before importing. Imported assets start
          unscored; classify and rate them during the walk.
        </Typography.Paragraph>
        <Table<CobieImportRow>
          dataSource={importRows ?? []}
          rowKey="key"
          /* AntD clamps the modal to the viewport, so on an iPad this table has to scroll
             within it rather than assume 960px. Row density eases off on touch — this is a
             grid of checkboxes and per-row selects. */
          size={touch ? 'middle' : 'small'}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 8, showTotal: (t, r) => `${r[0]}–${r[1]} of ${t}` }}
          rowSelection={{ selectedRowKeys: importSelected, onChange: setImportSelected }}
          columns={[
            { title: 'Asset ID', dataIndex: 'assetId', width: 110, render: v => <b>{v}</b> },
            { title: 'Description', dataIndex: 'description', ellipsis: true },
            { title: 'Location', dataIndex: 'location', width: 140, ellipsis: true },
            { title: 'Manufacturer', dataIndex: 'manufacturer', width: 130, ellipsis: true },
            { title: 'Model', dataIndex: 'model', width: 120, ellipsis: true },
            {
              title: 'System', dataIndex: 'system', width: 140,
              render: (v: SystemKey, r) => (
                <Select
                  size="small" style={{ width: '100%' }} value={v}
                  options={SYSTEM_TABS.map(t => ({ value: t.key, label: t.label }))}
                  onChange={sys => setImportRows(rows =>
                    rows!.map(x => x.key === r.key ? { ...x, system: sys } : x))}
                />
              ),
            },
          ]}
        />
      </Modal>

      {/* Paul's "Go To Table", in app. The workbook hyperlinks the RSL cell to that system's
          '* Life Expect. Table' sheet and leaves the assessor to read a number off it and do the
          subtraction; here the rows are searchable and applying one does the arithmetic. */}
      <Drawer
        title={`${SYSTEM_TABS.find(t => t.key === lifeTable?.key)?.label ?? ''} life expectancy table`}
        open={lifeTable !== null}
        onClose={() => setLifeTable(null)}
        width={narrow ? '100%' : 720}
        styles={{ body: { paddingTop: 12 } }}
      >
        {(() => {
          if (!lifeTable) return null;
          const asset = activeAssessment?.[lifeTable.key].assets.find(a => a.id === lifeTable.assetId) ?? null;
          const rows = LIFE_EXPECTANCY_ROWS[lifeTable.key] ?? [];
          const q = lifeTableFilter.trim().toLowerCase();
          const shown = q
            ? rows.filter(r => `${r.item} ${r.subItem}`.toLowerCase().includes(q))
            : rows;
          const age = asset?.age ?? null;   // null = not entered; RSL then waits for an age
          return (
            <>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 0 }}>
                Transcribed verbatim from the scorecard's <b>{SYSTEM_TABS.find(t => t.key === lifeTable.key)?.label} Life
                Expect. Table</b> sheet. Choosing a row sets this asset's description and its RSL
                (the row's expected life minus the asset's age{asset ? (age != null ? `, ${age} yr` : ' — not entered yet, so RSL will fill in once you add an age') : ''}).
              </Typography.Paragraph>
              <Input.Search
                allowClear placeholder="Search item or sub-item" value={lifeTableFilter}
                onChange={e => setLifeTableFilter(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              {shown.length === 0 ? (
                <Empty description={`Nothing matching “${lifeTableFilter}”`} />
              ) : (
                <Table<LifetimeRow>
                  dataSource={shown}
                  rowKey={r => `${r.item}|${r.subItem}`}
                  size={touch ? 'middle' : 'small'}
                  pagination={false}
                  scroll={{ y: '60vh' }}
                  columns={[
                    /* Item is short ("Air Handling Unit"); cap it so the Sub-item column gets the
                       room. Sub-item wraps instead of truncating so the full text is readable
                       without hovering. */
                    { title: 'Item', dataIndex: 'item', width: 160, ellipsis: true },
                    { title: 'Sub-item', dataIndex: 'subItem' },
                    { title: 'Years', dataIndex: 'years', width: 56, align: 'right' },
                    {
                      title: '', width: 80, align: 'right',
                      render: (_, r) => (
                        <Button
                          size="small" disabled={!asset || locked}
                          onClick={() => {
                            if (!asset || !activeAssessment) return;
                            /* Populate both fields from the picked row in one pass: the label's
                               format is "Item - Sub-item", and RSL is the row's expected life minus
                               the asset's age. Two sequential handleAssetFieldChange calls would
                               each rebuild from the same stale asset and drop the first field, so
                               map the list once here. */
                            const description = classificationLabel(r);
                            // No age → RSL stays blank; derivedRsl fills it when an age is entered.
                            const remainingServiceLife = age == null ? null : Math.max(0, r.years - age);
                            const updatedAsset = { ...asset, description, remainingServiceLife };
                            const assets = activeAssessment[lifeTable.key].assets.map(a =>
                              a.id === asset.id ? updatedAsset : a);
                            updateSystem(lifeTable.key, assets);
                            scheduleAssetSave(lifeTable.key, updatedAsset);
                            setLifeTable(null);
                          }}
                        >
                          Use
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </>
          );
        })()}
      </Drawer>

      <Modal
        title="New assessment"
        open={showCreate}
        onOk={handleCreateAssessment}
        okText={saving ? 'Creating…' : 'Create'}
        okButtonProps={{ disabled: saving || !newBuildingName.trim() }}
        onCancel={() => setShowCreate(false)}
      >
        <Input
          placeholder="Building name" autoFocus value={newBuildingName}
          onChange={e => setNewBuildingName(e.target.value)}
          onPressEnter={handleCreateAssessment}
        />
      </Modal>

      {!activeAssessment ? (
        <Card>
          <Typography.Title level={5}>No assessments yet</Typography.Title>
          <Typography.Paragraph type="secondary">Create one to get started.</Typography.Paragraph>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreate(true)} disabled={locked}>New Assessment</Button>
        </Card>
      ) : (
        <>
          {showInfo && (
            <Card title={<span><InfoCircleOutlined /> Facility &amp; Contact Information</span>} style={{ marginBottom: 16 }}>
              {infoGroups.map(g => (
                <div key={g.title} style={{ marginBottom: 18 }}>
                  {sectionHeader(g.title)}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                    {g.fields.map(f => (
                      <div key={f.field}>
                        <div style={{ fontSize: 13, marginBottom: 6 }}>{f.label}</div>
                        <Input
                          type={f.type}
                          value={(activeAssessment[f.field] as string) ?? ''}
                          onChange={e => handleInfoFieldChange(f.field, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Card>
          )}

          {/* Summary strip */}
          {/* Real responsive spans, not raw flex: five cards sharing one row put each in
              ~130px on an iPad, which is narrower than their own Statistic titles. Two-up
              in portrait, single row from xl. The second gutter value spaces the wrapped
              rows. */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12} xl={8}>
              {(() => {
                /* Score is out of 100 per assessed system, not a fixed 400 — recompute the
                   ceiling and the rating from it so a partial assessment reads correctly even if
                   the stored overallRating was written on the old fixed scale. */
                const maxScore = maxPossibleScore(activeAssessment);
                const overall = activeAssessment.overallScore ?? 0;
                // Nothing assessed yet → the ceiling is 0. Read "Not assessed", not 0/Failed.
                if (maxScore === 0) {
                  return (
                    <Card styles={{ body: { padding: '20px 24px' } }}>
                      <Statistic title="Overall building score" value="—" />
                      <div style={{ margin: '8px 0' }}>
                        <Tag>Not assessed</Tag>
                      </div>
                      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                        No assets assessed yet
                      </Typography.Text>
                    </Card>
                  );
                }
                const rating = getBuildingRating(overall, maxScore);
                const pct = Math.round((overall / maxScore) * 1000) / 10;
                return (
                  <Card styles={{ body: { padding: '20px 24px' } }}>
                    <Statistic
                      title="Overall building score"
                      value={Math.round(overall * 10) / 10}
                      suffix={<Typography.Text type="secondary" style={{ fontSize: 15 }}>/ {maxScore}</Typography.Text>}
                    />
                    <div style={{ margin: '8px 0' }}>
                      <RatingBadge rating={rating} />
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      {pct}% of possible · {RATING_DESCRIPTIONS[rating]}
                    </Typography.Text>
                  </Card>
                );
              })()}
            </Col>
            {SYSTEM_TABS.map(t => {
              const sys = activeAssessment[t.key];
              return (
                <Col xs={12} md={6} xl={4} key={t.key}>
                  <Card styles={{ body: { padding: '20px 24px' } }}>
                    <Statistic title={t.label} value={sys.rating ? Math.round((sys.score ?? 0) * 10) / 10 : '—'} />
                    <div style={{ margin: '8px 0' }}>
                      {sys.rating ? <RatingBadge rating={sys.rating} /> : <Tag>Not assessed</Tag>}
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>{sys.assets.length} assets</Typography.Text>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <Tabs
            activeKey={activeTab}
            onChange={k => { setActiveTab(k); setCompleteness('all'); }}
            items={tabItems}
            destroyOnHidden
            type="line"
            tabBarStyle={{ background: token.colorBgContainer, borderRadius: '8px 8px 0 0', padding: '0 24px', marginBottom: 0 }}
          />
        </>
      )}
    </div>
  );
};

export default FacilityModule;
